import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Save, X, Search, Edit2, Trash2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function FrivilligeTab({ token, eventId, eventName, onCountChange, showConfirm, closeConfirm }) {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState(null);
  const [newVolunteer, setNewVolunteer] = useState({ email: "", name: "", role: "", forening: "", hold: "" });

  const loadVolunteers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/events/${eventId}/volunteers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      setVolunteers(data);
      if (onCountChange) onCountChange(data.length);
    } catch (error) {
      console.error("Fejl ved hentning af frivillige", error);
    } finally {
      setLoading(false);
    }
  }, [eventId, token, onCountChange]);

  useEffect(() => { loadVolunteers(); }, [loadVolunteers]);

  const filteredVolunteers = volunteers.filter(v => {
    const term = searchTerm.toLowerCase();
    return (
      v.name?.toLowerCase().includes(term) ||
      v.email?.toLowerCase().includes(term) ||
      v.forening?.toLowerCase().includes(term) ||
      v.hold?.toLowerCase().includes(term)
    );
  });

  const addVolunteer = async () => {
    if (!newVolunteer.email || !newVolunteer.name || !newVolunteer.role) {
      toast.error("Udfyld email, navn og rolle");
      return;
    }
    try {
      await axios.post(`${API}/events/${eventId}/volunteers/add`, newVolunteer, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Frivillig tilføjet!");
      setShowAddForm(false);
      setNewVolunteer({ email: "", name: "", role: "", forening: "", hold: "" });
      loadVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved tilføjelse");
    }
  };

  const updateVolunteer = async () => {
    if (!editingVolunteer) return;
    try {
      const endpoint = editingVolunteer.source === 'excel_import'
        ? `${API}/events/${eventId}/imported-volunteers/${editingVolunteer.id}`
        : `${API}/events/${eventId}/volunteers/${editingVolunteer.id}`;
      await axios.put(endpoint, {
        name: editingVolunteer.name, email: editingVolunteer.email,
        role: editingVolunteer.role, forening: editingVolunteer.forening, hold: editingVolunteer.hold
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Frivillig opdateret!");
      setEditingVolunteer(null);
      loadVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering");
    }
  };

  const deleteVolunteer = (volunteer) => {
    showConfirm("Slet frivillig?", `Er du sikker på du vil slette "${volunteer.name}"?`, async () => {
      closeConfirm();
      try {
        const endpoint = volunteer.source === 'excel_import'
          ? `${API}/events/${eventId}/imported-volunteers/${volunteer.id}`
          : `${API}/events/${eventId}/volunteers/${volunteer.id}`;
        await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` }});
        toast.success("Frivillig slettet");
        loadVolunteers();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Fejl ved sletning");
      }
    });
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/volunteers/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Eksporteret ${res.data.count} frivillige`);
    } catch (error) {
      toast.error("Fejl ved eksport");
    }
  };

  const resolveFlaggedVolunteer = async (volunteerId, source, action) => {
    try {
      await axios.post(`${API}/events/${eventId}/volunteers/resolve-flagged`,
        { volunteer_id: volunteerId, source, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === 'keep' ? "Frivillig beholdt" : "Frivillig slettet");
      loadVolunteers();
    } catch (error) {
      toast.error("Fejl ved håndtering af frivillig");
    }
  };

  const resolveAllFlagged = async (action) => {
    try {
      await axios.post(`${API}/events/${eventId}/volunteers/resolve-all-flagged`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === 'keep_all' ? "Alle flaggede beholdt" : "Alle flaggede slettet");
      loadVolunteers();
    } catch (error) {
      toast.error("Fejl ved håndtering");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Søg efter navn, email, forening eller hold..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-volunteers" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">Eksporter CSV</Button>
          <Button onClick={() => setShowAddForm(true)} data-testid="add-volunteer-btn"><Plus className="h-4 w-4 mr-2" />Tilføj frivillig</Button>
        </div>
      </div>

      {showAddForm && (
        <div className="p-4 bg-card border border-border rounded-lg" data-testid="add-volunteer-form">
          <h3 className="font-bold mb-4">Tilføj ny frivillig</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Input placeholder="Email *" value={newVolunteer.email} onChange={(e) => setNewVolunteer({...newVolunteer, email: e.target.value})} data-testid="new-volunteer-email" />
            <Input placeholder="Navn *" value={newVolunteer.name} onChange={(e) => setNewVolunteer({...newVolunteer, name: e.target.value})} data-testid="new-volunteer-name" />
            <Input placeholder="Rolle *" value={newVolunteer.role} onChange={(e) => setNewVolunteer({...newVolunteer, role: e.target.value})} data-testid="new-volunteer-role" />
            <Input placeholder="Forening" value={newVolunteer.forening} onChange={(e) => setNewVolunteer({...newVolunteer, forening: e.target.value})} />
            <Input placeholder="Hold" value={newVolunteer.hold} onChange={(e) => setNewVolunteer({...newVolunteer, hold: e.target.value})} />
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={addVolunteer} data-testid="save-volunteer-btn"><Save className="h-4 w-4 mr-2" />Gem</Button>
            <Button variant="outline" onClick={() => setShowAddForm(false)}><X className="h-4 w-4 mr-2" />Annuller</Button>
          </div>
        </div>
      )}

      {volunteers.filter(v => v.flagged_for_review).length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div>
                <p className="font-medium text-red-400">{volunteers.filter(v => v.flagged_for_review).length} frivillig(e) var ikke i det seneste Excel-ark</p>
                <p className="text-sm text-muted-foreground">Gennemgå dem og vælg om de skal beholdes eller slettes</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => resolveAllFlagged('keep_all')} className="text-green-500 border-green-500/30 hover:bg-green-500/10"><Check className="h-4 w-4 mr-1" />Behold alle</Button>
              <Button size="sm" variant="outline" onClick={() => resolveAllFlagged('delete_all')} className="text-red-500 border-red-500/30 hover:bg-red-500/10"><Trash2 className="h-4 w-4 mr-1" />Slet alle</Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Henter frivillige...</div>
      ) : filteredVolunteers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">{searchTerm ? "Ingen resultater fundet" : "Ingen frivillige endnu"}</div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 text-xs uppercase tracking-wider font-mono">Navn</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider font-mono">Email</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider font-mono">Forening</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider font-mono">Hold</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider font-mono">Kilde</th>
                <th className="text-right p-3 text-xs uppercase tracking-wider font-mono">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {filteredVolunteers.map((v, idx) => (
                <tr key={v.id || idx} className={`border-t border-border hover:bg-muted/30 ${v.flagged_for_review ? 'bg-red-500/20 border-l-4 border-l-red-500' : ''}`} data-testid={`volunteer-row-${v.id}`}>
                  {editingVolunteer?.id === v.id ? (
                    <>
                      <td className="p-2"><Input value={editingVolunteer.name} onChange={(e) => setEditingVolunteer({...editingVolunteer, name: e.target.value})} className="h-8" /></td>
                      <td className="p-2"><Input type="email" value={editingVolunteer.email || ''} onChange={(e) => setEditingVolunteer({...editingVolunteer, email: e.target.value})} className="h-8" /></td>
                      <td className="p-2"><Input value={editingVolunteer.forening || ""} onChange={(e) => setEditingVolunteer({...editingVolunteer, forening: e.target.value})} className="h-8" /></td>
                      <td className="p-2"><Input value={editingVolunteer.hold || ""} onChange={(e) => setEditingVolunteer({...editingVolunteer, hold: e.target.value})} className="h-8" /></td>
                      <td className="p-3"><span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400">{v.source}</span></td>
                      <td className="p-3 text-right">
                        <Button size="sm" onClick={updateVolunteer} className="mr-1"><Save className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingVolunteer(null)}><X className="h-3 w-3" /></Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-3 font-medium">{v.name}</td>
                      <td className="p-3 text-sm text-muted-foreground">{v.email}</td>
                      <td className="p-3 text-sm">{v.forening || "-"}</td>
                      <td className="p-3 text-sm">{v.hold || "-"}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-1 rounded ${v.source === 'excel_import' ? 'bg-green-500/20 text-green-400' : v.source === 'excel_legacy' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {v.source === 'excel_import' ? 'Import' : v.source === 'excel_legacy' ? 'Gammel Excel' : 'Manuel'}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {v.flagged_for_review ? (
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-red-400 mr-2">Ikke i Excel</span>
                            <Button size="sm" variant="outline" onClick={() => resolveFlaggedVolunteer(v.id, v.source === 'excel_import' ? 'imported' : 'manual', 'keep')} className="text-green-500 hover:bg-green-500/10" title="Behold frivillig"><Check className="h-3 w-3" /></Button>
                            <Button size="sm" variant="outline" onClick={() => resolveFlaggedVolunteer(v.id, v.source === 'excel_import' ? 'imported' : 'manual', 'delete')} className="text-destructive hover:bg-destructive/10" title="Slet frivillig"><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        ) : v.can_edit && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => setEditingVolunteer(v)} className="mr-1"><Edit2 className="h-3 w-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteVolunteer(v)} className="text-destructive hover:bg-destructive/10"><Trash2 className="h-3 w-3" /></Button>
                          </>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
